import { CommonModule } from '@angular/common';
import { Component, Optional, ViewEncapsulation, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import {
  FileUploadModule,
} from 'primeng/fileupload';
import { ChipModule } from 'primeng/chip';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { constants } from '../../../../../shared/constants/constants';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { Id, ReviewerCredential } from '../credentials.model';
import { Reviewer } from '../../../../../store/reviewer/reviewer.interface';
import { DynamicCredentialService } from '../../../../../shared/services/dynamic-credential.service';
import { ReviewerService } from '../../../../../shared/services/reviewer.service';
import { DocumentService } from '../../../../../shared/services/document-service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageService } from '../../../../../shared/services/storage.service';
import { EmittersService } from '../../../../../shared/services/emitters.service';
import { TooltipModule } from 'primeng/tooltip';
import { HelperService } from '../../../../../shared/services/helper.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ConfigurationService } from '../../../../../shared/services/configuration.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserService } from '../../../../../shared/services/user.service';

@Component({
  selector: 'app-add-credentials',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FloatLabelModule,
    CalendarModule,
    InputTextModule,
    DropdownModule,
    MultiSelectModule,
    TagModule,
    FileUploadModule,
    ChipModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    TooltipModule,
    PipesModule,
    FormsModule,
    InputSwitchModule
  ],
  templateUrl: './add-credentials.component.html',
  styleUrl: './add-credentials.component.scss',
  encapsulation: ViewEncapsulation.None
  
})
export class AddCredentialsComponent {

  /**
   * MP-5736: the CHILD firm whose view of this reviewer we are editing, or ''
   * when there is none - which is every reviewer, every standalone firm and,
   * until the MP-5643 switcher lands, every parent-level FM. Sending '' means
   * "today's path", so this call is a no-op for all of them.
   */
  private readonly actingFirmUserService = inject(UserService);
  credentialForm!: FormGroup;
  breadcrumbs: MenuItem[] = [];
  userFirmId: string = '';
  dynamicCredentials!: Id[];
  reviewer!: Reviewer;
  reviewerCredentials!: ReviewerCredential[];
  secondaryOptions!: string[];
  dropDownList: ReviewerCredential[] = [{}];
  maxIssuedAt!: Date;
  minExpiresAt!: Date;
  secondaryOptionsDropdown: string[] | undefined = [];
  file!: File | null;
  uploadedDocumentId: string = '';
  firmName!: string;
  credName!: string;
  isYearlyRenewal: boolean = false;
  editData!: ReviewerCredential;
  editId!: string | null;
  add: string = constants.dynamicCredential.titleText.add;
  edit: string = constants.dynamicCredential.titleText.edit;
  formValid = true;
  isFormSubmitWithoutFile = false;
  headingText = 'Secondary Options';
  isPwcEnabled = false;
  fromOnboarding = false;

  constructor(
    private fb: FormBuilder,
    private dynamicCredentialService: DynamicCredentialService,
    private reviewerService: ReviewerService,
    private documentService: DocumentService,
    private toastService: ToastService,
    private router: Router,
    private storageService: StorageService,
    private emitterService: EmittersService,
    private route: ActivatedRoute,
    public helperService: HelperService,
    public configurationService: ConfigurationService,
    @Optional() private ref: DynamicDialogRef, 
    @Optional() public dialogConfig: DynamicDialogConfig,
  ) {
    this.emitterService.tenantEmitter.subscribe((event) => {
      this.ngOnInit();
    });
  }

  async ngOnInit(): Promise<void> {
    this.isPwcEnabled = this.configurationService.getPwcFlag();
    this.initializeForm();
    if(this.dialogConfig && this.dialogConfig.data){
      this.userFirmId = this.dialogConfig.data.firmId;
      // MP-5727: the FM profile opens this dialog in edit mode by passing the
      // record _id; everything downstream (single-value control, locked
      // credential, populateValues) already keys off editId
      this.editId = this.dialogConfig.data.editId || null;
      await this.getReviewerDetails(this.dialogConfig.data.userId);
    }
    else{
      this.userFirmId = this.storageService.getTenant()?.firm?._id;
      this.editId = this.route.snapshot.paramMap.get('id');
      this.fromOnboarding = this.route.snapshot.queryParamMap.get('from') === 'onboarding';
      await this.getReviewerInfo();
    }
    await this.getDynamicCredentials();
    await this.getReviewerCredentials();
    let firmName  = '';
    const selectedTenant = this.storageService.getTenant();
    if(selectedTenant){
      firmName = selectedTenant.firm?.name || '';
    }
    this.setDates();
    
    if(this.secondaryOptionsDropdown){
      this.secondaryOptionsDropdown.length = 0;
    }
    
    this.breadcrumbs = [
      { label: firmName },
      { label: 'Profile' },
      { label: 'Credentials' },
      { label: this.editId ? 'Edit credential' : 'Add credential' }
    ]
    if (this.editId) {
      // MP-5739: records have their own unique _id (MP-5737), so the id alone
      // identifies the record — no more state/secondaryOptions disambiguation
      const data = this.reviewerCredentials.find((cred) => cred._id === this.editId);
      if (data) {
        this.editData = data;
        this.populateValues();
      }
    } else {
      this.addCredentialsDropdown();
    }
    if(!this.dialogConfig){
      await lastValueFrom(this.reviewerService.updateProfileStrengthByTenant(this.userFirmId,{credentials:true}));
    }
  }

  initializeForm(): void {
    this.credentialForm = this.fb.group({
      credentials: ['', [Validators.required]],
      issuedAt: ['', Validators.required],
      expiresAt: [''],
      secondaryOptions: [],
    });
    if (this.isPwcEnabled) {
      this.credentialForm.addControl('membershipNumber', this.fb.control('', [Validators.pattern(constants.onlyLettersAndNumbersRegex)]));
      // MP-5727: the Active switch is the reviewer's flag. The FM dialog
      // (add and edit) has no switch — the firm deactivates via the profile
      // row and restores from Removed Credentials, which write status only.
      if (!this.dialogConfig) {
        this.credentialForm.addControl('isReviewerActivated', this.fb.control(true));
      }
    }
  }

  async populateValues() {
    const desc = this.dynamicCredentials.find((cred: any) => cred._id === this.editData?.credentialId)?.description || '';
    const trimmedDesc = this.helperService.trimLetters(desc, 50);
    const editCredential: ReviewerCredential = {
      _id: this.editData._id,
      autoRenewal: this.editData.isYearlyRenewal || false,
      isYearlyRenewal: this.editData.isYearlyRenewal || false,
      isDocumentationRequired: this.editData.isDocumentationRequired || false,
      name: this.editData.name || '',
      secondaryOptions: this.editData?.secondaryOptions,
      credentialId: this.editData.credentialId || '',
      displayName: desc ? `${this.editData.name} - ${trimmedDesc}` : this.editData.name,
      membershipNumber: this.editData.membershipNumber || '',
      // MP-5727: carried exactly as stored — defaulting it to true turned a
      // reviewer's switch-off back on
      isReviewerActivated: this.editData.isReviewerActivated,
    };
    this.dropDownList.push(editCredential);
    this.dropDownList.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
    this.credentialForm.get('credentials')?.setValue(editCredential);
    this.credentialForm.controls['credentials'].disable();
    const issuedAt = this.editData.issuedAt?.toString() || '';
    this.credentialForm.get('issuedAt')?.setValue(new Date(issuedAt));
    if(this.editData.expiresAt){
      const expiresAt = this.editData.expiresAt?.toString() || '';
      this.credentialForm.get('expiresAt')?.setValue(new Date(expiresAt));
    }

    // MP-5739: a record holds exactly one value, so edit mode is a
    // single-value control for both tenants. Values held by the reviewer's
    // OTHER records are filtered out; the record's own value stays offered.
    const ownValue = this.editData.secondaryOptions || '';
    const heldByOtherRecords = this.heldValuesByCredential(this.editData._id);

    // Set up credential-specific data for edit mode. A record written before
    // the MP-5738 migration carries the definition id as its _id, hence the
    // fallback.
    const definitionId = this.editData.credentialId || this.editData._id || '';
    const selectedCredential = this.dynamicCredentials.find(cred => cred._id === definitionId);
    if (selectedCredential) {
      this.credName = selectedCredential.name || '';
      this.isYearlyRenewal = selectedCredential.isYearlyRenewal || false;

      // Secondary options dropdown: the definition's values minus the ones
      // already held on other records of this credential
      const options = JSON.parse(selectedCredential.secondaryOptions.options);
      const heldValues = heldByOtherRecords[definitionId] || {};
      this.secondaryOptionsDropdown = options.values
        .map((obj: any) => obj.val)
        .filter((val: string) => val === ownValue || !heldValues[val]);

      // Set heading text based on credential type
      if (this.isPwcEnabled && this.isBarMembership(this.credName) && selectedCredential.isGlobal) {
        this.headingText = 'State';
        // Make state required for bar membership
        this.credentialForm.get('secondaryOptions')?.setValidators([Validators.required]);
        this.credentialForm.get('secondaryOptions')?.updateValueAndValidity();
      } else {
        this.headingText = 'Secondary Options';
      }
    } else {
      // Fallback to existing data if credential definition not found
      this.secondaryOptionsDropdown = ownValue ? [ownValue] : [];
    }
    // A record written before the MP-5738 migration can still hold a
    // comma-joined value; keep it selectable so a resubmit round-trips it
    // (the backend fans it out into one record per value).
    if (ownValue && this.secondaryOptionsDropdown && !this.secondaryOptionsDropdown.includes(ownValue)) {
      this.secondaryOptionsDropdown.unshift(ownValue);
    }

    this.credentialForm.get('secondaryOptions')?.setValue(ownValue);
    if(this.isPwcEnabled) {
      this.credentialForm.controls['issuedAt'].disable();
      this.credentialForm.get('membershipNumber')?.setValue(this.editData.membershipNumber);
      this.credentialForm.get('isReviewerActivated')?.setValue(this.editData.isReviewerActivated);
    }
    if(this.editData.certificate){
      const blob = new Blob([''], { type: this.editData?.certificate?.mimeType });
      this.file = new File([blob], this.editData.certificate?.title || '', {
        type: this.editData.certificate?.mimeType,
      });
    }
  }

  /**
   * MP-5744: the PWC global definition is stored as "Bar membership"
   * (lowercase m), while the constant is "Bar Membership" — the former
   * case-sensitive equality never matched, so the State label and the
   * required validator never fired on PWC. Compare trimmed and lowercased.
   * Stopgap only: MP-5726 replaces this name check with a flag on the
   * definition and removes it.
   */
  isBarMembership(name: string | undefined): boolean {
    return (name || '').trim().toLowerCase()
      === constants.dynamicCredential.barMembership.trim().toLowerCase();
  }

  setDates(): void {
    this.maxIssuedAt = new Date();
    this.credentialForm
      .get('issuedAt')
      ?.valueChanges.subscribe((issuedAt: Date) => {
        if (issuedAt) {
          this.minExpiresAt = new Date(issuedAt);
          this.minExpiresAt.setDate(this.minExpiresAt.getDate() + 1);
        }
      });
  }

  /**
   * MP-5739: the values the reviewer already holds, per credential definition.
   * Used to offer only the values that would not duplicate an existing
   * record — the credential itself stays selectable while values remain.
   */
  heldValuesByCredential(excludeRecordId?: string | null): Record<string, Record<string, boolean>> {
    const held: Record<string, Record<string, boolean>> = {};
    (this.reviewerCredentials || []).forEach((record) => {
      if (excludeRecordId && record._id === excludeRecordId) {
        return;
      }
      // records written before the MP-5738 migration carry the definition id as _id
      const definitionId = record.credentialId || record._id || '';
      if (!held[definitionId]) {
        held[definitionId] = {};
      }
      (record.secondaryOptions || '').split(',').forEach((value) => {
        held[definitionId][value] = true;
      });
    });
    return held;
  }

  addCredentialsDropdown(): void {
    this.dropDownList.length = 0;
    const held = this.heldValuesByCredential();
    this.dynamicCredentials.forEach((cred: Id) => {
      const mapped = this.mapCredentials(cred, held[cred._id]);
      if (mapped) {
        this.dropDownList.push(mapped);
      }
    });
    this.dropDownList.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }

  /**
   * Maps a credential definition into a dropdown entry carrying only the
   * secondary values the reviewer does not hold yet. Returns null when
   * nothing is left to add (every value held, or a value-less credential
   * already held) — adding it again could only create a duplicate record.
   * Deliberately NO _id: the record id is the server's to assign (MP-5737).
   */
  mapCredentials(cred: Id, heldValues?: Record<string, boolean>): ReviewerCredential | null {
    const options = JSON.parse(cred.secondaryOptions.options);
    const remainingValues = options.values
      .map((obj: any) => obj.val)
      .filter((val: string) => !heldValues || !heldValues[val]);
    if (heldValues && remainingValues.length === 0) {
      return null;
    }
    const trimmedDesc = this.helperService.trimLetters(cred.description, 50);

    const mappedCred: ReviewerCredential = {
      autoRenewal: cred.isYearlyRenewal || false,
      isYearlyRenewal: cred.isYearlyRenewal || false,
      isDocumentationRequired: cred.isDocumentationRequired || false,
      name: cred.name || '',
      secondaryOptions: remainingValues.join(','),
      credentialId: cred._id,
      displayName: cred.description ? `${cred.name} (${trimmedDesc})` : cred.name,
      isGlobal: cred.isGlobal || false
    };
    return mappedCred;
  }

  onDropdownChange(value: ReviewerCredential): void {
    this.credentialForm.get('secondaryOptions')?.setValue('');
    this.secondaryOptionsDropdown = value?.secondaryOptions
      ?.split(',')
      .filter((value) => value.trim() !== '' );
    this.credName = value?.name || '';
    this.isYearlyRenewal = value.isYearlyRenewal || false;
    
    // Set heading text and validation based on credential type
    if (this.isPwcEnabled && this.isBarMembership(this.credName) && value.isGlobal) {
      this.headingText = 'State';
      // Make state required for bar membership
      this.credentialForm.get('secondaryOptions')?.setValidators([Validators.required]);
    } else {
      this.headingText = 'Secondary Options';
      // Remove required validation for non-bar membership credentials
      this.credentialForm.get('secondaryOptions')?.setValidators([]);
    }
    this.credentialForm.get('secondaryOptions')?.updateValueAndValidity();
  }

  async getDynamicCredentials(): Promise<void> {
    this.dynamicCredentials = await lastValueFrom(
      this.reviewerService.getFilteredCredentialBasedUserSkills(this.reviewer._id)
    );
    this.firmName = this.dynamicCredentials[0]?.firmName || "";
  }

  async getReviewerCredentials(): Promise<void> {
    this.reviewerCredentials = await lastValueFrom(
      this.dynamicCredentialService.getReviewerCredentials(this.reviewer._id, this.actingFirmUserService.getActingChildFirmId())
    );
    // MP-5739: the dialog used to hide every credential the reviewer already
    // holds, which blocked adding a second value (e.g. a second Bar
    // Membership state). Filtering now happens per VALUE in
    // addCredentialsDropdown, for the dialog and the reviewer screen alike.
  }

  async getReviewerDetails(reviewerId: string): Promise<void> {
    this.reviewer = await lastValueFrom(this.reviewerService.getReviewerProfile(reviewerId, this.userFirmId));
  }

  async getReviewerInfo(): Promise<void> {
    this.reviewer = await lastValueFrom(this.reviewerService.fetchReviewer());
  }

  async uploadDocToServer(document: File | null) {
    const opts = {
      documentType: 'CREDENTIALS_CERTIFICATE',
      reviewerId: this.reviewer._id,
      isAdminUploaded: false,
    };
    const res = await lastValueFrom(
      this.documentService.uploadDocument(document, opts)
    );
    this.uploadedDocumentId = res.documentIds;
  }

  async onFileSelect($event: Event): Promise<void> {
    const input = $event.target as HTMLInputElement;
    this.file = input.files ? input.files[0] : null;
    if(this.file && this.file.type === 'application/pdf'){
      const isValid = await this.helperService.validatePdf(this.file);
      if (!isValid) {
        this.toastService.showError("Corrupted or invalid PDF file.");
        this.removeFile();
        return;
      }
      this.isFormSubmitWithoutFile = false;
    }
    input.value = '';
  }

  removeFile() {
    this.file = null;
  }

  updateReviewerCredentials(reviewerCreds: ReviewerCredential[]): void {
    this.dynamicCredentialService
      .updateReviewerDynamicCredentials({
        reviewerId: this.reviewer._id,
        credentials: reviewerCreds,
        deleteKey: false,
        // MP-5736: routes the write to this child's own records (MP-5734).
        actingFirmId: this.actingFirmUserService.getActingChildFirmId()
      })
      .subscribe({
        next: (res) => {
          this.toastService.showSuccess(this.editData ? 'Credential Updated Successfully' : 'Credentials Added Successfully');
          this.onDiscard(true);
        },
      });
  }

  async onSubmit(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    if (!await this.validateAndUploadDocument()) {
      return;
    }

    if (this.credentialForm.invalid) {
      this.toastService.showError('Please complete all required fields');
      return;
    }

    const basePayload = this.createCredentialPayload();
    const records = this.buildCredentialRecords(basePayload, this.credentialForm.get('secondaryOptions')?.value);
    this.updateCredentialsList(records);
    this.finalizeSubmission();
  }

  private validateForm(): boolean {
    this.formValid = false;
    if (!this.credentialForm.valid) {
      return false;
    }
    this.formValid = true;
    return true;
  }

  private async validateAndUploadDocument(): Promise<boolean> {
    const isDocumentationRequired = this.isDocumentationRequired();
    
    if (isDocumentationRequired && !this.file) {
      this.isFormSubmitWithoutFile = true;
      this.toastService.showError('Documentation required for this credential record');
      return false;
    }
    
    if (this.file) {
      await this.uploadDocToServer(this.file);
    }
    
    return true;
  }

  private isDocumentationRequired(): boolean {
    return this.dynamicCredentials.find(
      (cred) => cred._id === this.credentialForm.get('credentials')?.value?.credentialId
    )?.isDocumentationRequired || false;
  }

  /**
   * MP-5739: the base payload carries NO definition id as _id. A new record
   * carries credentialId only (the server assigns the record id, MP-5737);
   * an edited record carries its own _id.
   */
  private createCredentialPayload(): ReviewerCredential {
    let payload: ReviewerCredential = {
      ...this.credentialForm.get('credentials')?.value,
      issuedAt: this.credentialForm.get('issuedAt')?.value,
      expiresAt: this.credentialForm.get('expiresAt')?.value,
      certificate: this.uploadedDocumentId || null,
    };

    // MP-5727: only a NEW record is stamped as FM-created. An edited record
    // keeps its original creator — re-stamping a reviewer-added record as
    // FM-created made the backend's createdBy filter hide it from the
    // reviewer's own credentials screen.
    if (this.editData) {
      payload.createdBy = this.editData.createdBy;
    } else if (this.dialogConfig) {
      payload.createdBy = constants.userRoles.firmManager;
    }

    if (this.isPwcEnabled) {
      payload.membershipNumber = this.credentialForm.get('membershipNumber')?.value;
      // MP-5727: only the reviewer's own form writes the switch and status.
      // The FM dialog never does: a new record takes the backend defaults,
      // an edited record keeps its stored values (merged in
      // updateExistingCredential), so an FM save cannot flip the reviewer's
      // switch or undo a firm deactivation.
      if (!this.dialogConfig) {
        payload.isReviewerActivated = this.credentialForm.get('isReviewerActivated')?.value;
        payload.status = payload.isReviewerActivated ? 'Active' : 'Inactive';
      }
    }

    return payload;
  }

  /**
   * One record per selected value, each copying the same dates, document,
   * membership number and status. Edit mode is single-valued (a record holds
   * one value), so it always yields exactly one record.
   */
  private buildCredentialRecords(payload: ReviewerCredential, secondaryOptions: any): ReviewerCredential[] {
    const values: string[] = Array.isArray(secondaryOptions)
      ? secondaryOptions
      : (secondaryOptions ? [secondaryOptions] : []);
    if (!values.length) {
      return [{ ...payload, secondaryOptions: '' }];
    }
    return values.map((value) => ({ ...payload, secondaryOptions: value }));
  }

  private updateCredentialsList(records: ReviewerCredential[]): void {
    if (this.editData) {
      this.updateExistingCredential(records[0]);
    } else {
      this.reviewerCredentials.push(...records);
    }
  }

  private updateExistingCredential(payload: ReviewerCredential): void {
    const existingRecordIndex = this.reviewerCredentials.findIndex(
      (record) => record._id === this.editData._id
    );
    if (existingRecordIndex !== -1) {
      // MP-5727: merge over the stored record instead of replacing it, so
      // fields the form does not own (createdBy, status, verification,
      // addedAt) survive the edit. The certificate is kept unless a new
      // file was uploaded.
      const existing = this.reviewerCredentials[existingRecordIndex];
      this.reviewerCredentials[existingRecordIndex] = {
        ...existing,
        ...payload,
        certificate: payload.certificate || existing.certificate || null,
      } as ReviewerCredential;
    }
  }

  private finalizeSubmission(): void {
    this.isFormSubmitWithoutFile = false;
    this.updateReviewerCredentials(this.reviewerCredentials);
  }

  onDiscard(param: boolean): void {
    this.credentialForm.reset();
    this.removeFile();
    if (!this.dialogConfig) {
      if (this.fromOnboarding) {
        const firmId = this.storageService.getTenant()?.firm?._id;
        this.router.navigate(['/onboarding'], { queryParams: { firm: firmId, step: 'credentials' } });
      } else {
        this.router.navigateByUrl('/reviewer/profile/credentials');
      }
    }
    else{
      this.ref.close(param); 
    }
  }

  onIssueDateChange(issueDate: Date): void {
  if (issueDate && this.isPwcEnabled) {
    const expiresAt = new Date(issueDate);
    expiresAt.setFullYear(new Date().getFullYear() + 1); 
    this.credentialForm.get('expiresAt')?.setValue(expiresAt);
  }
}
} 